import { useMemo } from 'react';
import styled from 'styled-components';
import { Alert } from '@zillow/constellation';
import { useSelector } from 'react-redux';
import { listingSelector, primaryApplicantNameSelector, userSelector } from '../../selectors';

export const Steps = {
  ReasonsStep: 'ReasonsStep',
  PreviewStep: 'PreviewStep',
  ConfirmationStep: 'ConfirmationStep',
  WithdrawnErrorStep: 'WithdrawnErrorStep',
};

export const CONSUMER_REPORT_REQ = 'CONSUMER_REPORT';
export const WITHDRAWN_ERROR = 'BAD_REQUEST_APPLICATION_IS_WITHDRAWN';
export const ALREADY_DECLINED_ERROR = 'APPLICATION_ALREADY_DECLINED';

export const checkIsBasedOnScreening = (selectedDeclineReasons, allDeclineReasons) => {
  // no need to useMemo here, so long as we skip calculations when Set size is 0
  if (!selectedDeclineReasons.size) {
    return false;
  }
  return allDeclineReasons
    .filter((r) => r.additionalRequirement === CONSUMER_REPORT_REQ)
    .some((fr) => selectedDeclineReasons.has(fr.name));
};

export const BorderlessAlert = styled(Alert)`
  border: none;
  margin-top: ${({ marginTop = 0 }) => marginTop}px;
  margin-bottom: ${({ marginBottom = 0 }) => marginBottom}px;
`;

export const Capitalized = styled.span`
  text-transform: capitalize;
`;

export const useEmailTemplate = (selectedDeclineReasons, allDeclineReasons) => {
  const { ireneFirstName, ireneLastName } = useSelector(userSelector);
  const { street, unit, city, state } = useSelector(listingSelector);
  const { firstName: rachelFirstName } = useSelector(primaryApplicantNameSelector);
  const declineReasonMap = allDeclineReasons?.reduce((map, reason) => map.set(reason.name, reason), new Map());

  return useMemo(
    () =>
      selectedDeclineReasons.size === 0
        ? ''
        : `\
Hi ${rachelFirstName},

Thanks for your interest in ${street},${unit ? ` ${unit},` : ''} ${city}, ${state}.
Unfortunately, your rental application for this property has been declined.

Your application was declined due to:

${[...selectedDeclineReasons].map((reasonName) => `  - ${declineReasonMap?.get(reasonName).text}\n`).join('')}
I appreciate your time and I wish you the best of luck in your rental search.

Thank you.\
${ireneFirstName && ireneLastName ? `\n\n${ireneFirstName} ${ireneLastName}` : ''}\
`,
    // we only care about the size of decline reasons, not the variable itself
    [city, rachelFirstName, state, street, unit, selectedDeclineReasons.size],
  );
};

export const MAX_EMAIL_COUNT = 4000;

// all letters from any language, numbers, punctuation, whitespace separators
export const UNICODE_REGEX = /[^\p{L}\p{N}\p{P}\p{Z}^$~`\n\+=\|<>\\\\]/gu;

// exported here for unit testability
export const stripNonUnicodeOnInput = ({ target }) => {
  // only manually update cursor if characters will indeed be stripped from text
  if (UNICODE_REGEX.test(target.value)) {
    const position = target.selectionStart;
    const newSelectionEnd = position - target.value.match(UNICODE_REGEX).length;

    target.value = target.value.replace(UNICODE_REGEX, '');
    target.selectionEnd = newSelectionEnd;
  }
};
